import { useEffect } from "react";
import { createColumnHelper } from "@tanstack/react-table";
import { useThunkDispatch } from "setup/redux/thunkDispatch";
import {
  getBalancesForAllProperties,
  selectClientPropertyBalances,
  selectLoadingClientPropertyBalances,
} from "./components/PropertyBalancesTableSlice";
import { ClientPropertyBalanceItemModel } from "services/api/cplapi/CPLAPI";
import CPLTable from "cpl-react-common/components/Tables/CPLTable";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { ActivityIndicator } from "cpl-react-common/components/ActivityIndicators/ActivityIndicator";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faHouse } from "@fortawesome/pro-solid-svg-icons";
import { selectGlobalSettings } from "app/modules/system/settings/ClientPortalSettingsSlice";
import translations from "utils/translationUtility";

const PropertyBalancesTable: React.FC = () => {
  const thunkDispatch = useThunkDispatch();
  const navigate = useNavigate();
  const propertyBalances = useSelector(selectClientPropertyBalances);
  const isLoading = useSelector(selectLoadingClientPropertyBalances);
  const globalSettings = useSelector(selectGlobalSettings);

  const columnHelper = createColumnHelper<ClientPropertyBalanceItemModel>();
  const columnDefinition = [
    columnHelper.display({
      id: "icon",
      cell: () => (
        <div className="d-flex">
          <FontAwesomeIcon icon={faHouse} size="lg" className="cpl-brand-primary" />
        </div>
      ),
      enableHiding: false,
    }),
    columnHelper.accessor((row) => row.clientAccountNumber, {
      id: "Client Account Number",
      cell: (info) => info.getValue(),
      header: () => <>{translations.client} Number</>,
      enableColumnFilter: false,
    }),
    columnHelper.accessor((row) => row.propertyAddress, {
      id: "Address",
      cell: (info) => info.getValue(),
      header: () => <>Address</>,
      enableColumnFilter: false,
    }),
    columnHelper.accessor((row) => row.outstandingBalance, {
      id: "Outstanding Balance",
      cell: (info) => info.getValue()!.toGBPString(),
      header: () => <>Outstanding Balance</>,
      enableColumnFilter: false,
      meta: {
        columnClassName: "currency-column-header",
        dataClassName: "text-end",
      },
    }),
    columnHelper.accessor((row) => row.eae, {
      id: "Total EAE",
      cell: (info) => (info.getValue() !== 0 ? info.getValue()!.toGBPString() : "N/A"),
      header: () => <div title="Estimated Annual Expenditure">Total EAE</div>,
      enableColumnFilter: false,
      meta: {
        columnClassName: "currency-column-header",
        dataClassName: "text-end",
      },
    }),
    columnHelper.display({
      id: "actions",
      cell: (info) =>
        info.row.original.hasTransactions && (
          <div className="d-flex gap-2">
            <div
              className="cpl-btn btn-sm cpl-btn-outline"
              onClick={() => navigateToTransactions(info.row.original)}
              data-test-id={`property-balances-table__view-transactions-link-${info.row.original.clientID}`}
              data-bs-toggle="tooltip"
              data-bs-placement="top"
            >
              View Transactions
            </div>
            {globalSettings.showBudgets && info.row.original.isBudgeted && (
              <div
                className="cpl-btn btn-sm cpl-btn-outline"
                onClick={() => navigateToBudgets(info.row.original)}
                data-test-id={`property-balances-table__view-budgets-link-${info.row.original.clientID}`}
                data-bs-toggle="tooltip"
                data-bs-placement="top"
              >
                View Budgets
              </div>
            )}
          </div>
        ),
      enableHiding: false,
    }),
  ];
  const defaultSorting = [
    {
      id: "Client Account Number",
      desc: false,
    },
  ];

  useEffect(() => {
    thunkDispatch(getBalancesForAllProperties());
  }, [thunkDispatch]);

  const navigateToTransactions = (propertyRow: ClientPropertyBalanceItemModel) => {
    if (!propertyRow.hasTransactions) return;
    const path = `/ClientTransactions/${propertyRow.clientID}`;
    navigate(path);
  };

  const navigateToBudgets = (propertyRow: ClientPropertyBalanceItemModel) => {
    if (!propertyRow.hasTransactions) return;
    const path = `/ClientBudgets/${propertyRow.clientID}`;
    navigate(path);
  };

  return (
    <div className="card d-flex mt-4 cpl-table-container">
      <ActivityIndicator isBusy={isLoading}>
        <div className="card-body">
          <CPLTable
            data={propertyBalances.propertyBalances}
            columns={columnDefinition}
            rowMobileClickAction={(row) => navigateToTransactions(row)}
            sortingState={defaultSorting}
            classNames="cpl-table-nowrap-below-desktop"
          />
        </div>
      </ActivityIndicator>
    </div>
  );
};

export default PropertyBalancesTable;
